import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { File as FileIcon, FileCode, FileImage, FileSpreadsheet, FileText, Info, Presentation } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

const mimeTypeIcons: Record<string, LucideIcon> = {
    'application/pdf': FileText,
    'text/plain': FileText,
    'text/markdown': FileText,
    'text/html': FileCode,
    'text/csv': FileSpreadsheet,
    'application/json': FileCode,
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': FileText,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': FileSpreadsheet,
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': Presentation,
    'application/msword': FileText,
    'image/png': FileImage,
    'image/jpeg': FileImage,
};

export function getFileIcon(mimeType: string): LucideIcon {
    return mimeTypeIcons[mimeType] || FileIcon;
}

export async function downloadResponseAsFile(res: Response, fallbackName: string): Promise<void> {
    const blob = await res.blob();
    const header = res.headers.get('content-disposition');
    const match = header?.match(/filename="?([^";]+)"?/);
    const filename = match?.[1] || fallbackName;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}


export function InfoPopover({ children, ariaLabel = 'More information' }: {
    children: ReactNode;
    ariaLabel?: string;
}) {
    const [open, setOpen] = useState(false);
    const [hovered, setHovered] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const closeTimer = useRef<number | null>(null);
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
    const visible = open || hovered;

    const handleEnter = () => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
        setHovered(true);
    };

    const handleLeave = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = window.setTimeout(() => {
            setHovered(false);
            closeTimer.current = null;
        }, 200);
    };

    useEffect(() => () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
    }, []);

    useLayoutEffect(() => {
        if (!visible) return;
        const updatePosition = () => {
            if (!buttonRef.current) return;
            const rect = buttonRef.current.getBoundingClientRect();
            setPosition({ top: rect.bottom, left: rect.right });
        };
        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [visible]);

    return (
        <div className="shrink-0">
            <button
                ref={buttonRef}
                type="button"
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (visible) {
                        if (closeTimer.current) {
                            clearTimeout(closeTimer.current);
                            closeTimer.current = null;
                        }
                        setOpen(false);
                        setHovered(false);
                    } else {
                        setOpen(true);
                    }
                }}
                className="flex items-center text-coolslate/60 hover:text-brand transition-colors"
                aria-label={ariaLabel}
            >
                <Info className="w-4 h-4" />
            </button>
            {visible && position && createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: position.top,
                        left: position.left,
                        transform: 'translateX(-100%)',
                    }}
                    className="z-50 pt-1"
                    onMouseEnter={handleEnter}
                    onMouseLeave={handleLeave}
                >
                    <div className="bg-white border border-softgrey rounded-lg shadow-lg p-3">
                        {children}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

export function MetadataInfo({ metadata }: { metadata?: Record<string, any> | null }) {
    if (!metadata || Object.keys(metadata).length === 0) return null;
    return (
        <InfoPopover ariaLabel="Show file metadata">
            <div className="w-72 space-y-2 max-h-64 overflow-y-auto text-xs">
                {Object.entries(metadata).map(([key, value]) => (
                    <div key={key} className="flex flex-col gap-0.5">
                        <span className="font-medium text-graphite">{key}</span>
                        <span className="text-coolslate font-mono break-all">
                            {typeof value === 'object' && value !== null
                                ? JSON.stringify(value)
                                : String(value)}
                        </span>
                    </div>
                ))}
            </div>
        </InfoPopover>
    );
}
